import { normalizeValue } from './statusNormalization';
import {
  copyNonEmptyRecordFields,
  getFirstRawRecordValue,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordSizeInBytes,
  getRecordTranscriptFileId,
  getRecordLogicalName,
  isPlainObject
} from './caseFileRecordAccessors';

export function createCaseFileRecordNormalizationHelpers({
  chooseBetterCaseFileRecord,
  getRecordScanStatus,
  getRecordLifecycleStatus
}) {
  function hasMeaningfulTranscriptRecord(record) {
    if (!record) {
      return false;
    }

    return Boolean(
      getRecordTranscriptFileId(record)
      || getRecordLogicalName(record)
      || getRecordScanStatus(record)
      || getRecordLifecycleStatus(record)
      || getRecordSizeInBytes(record)
      || getFirstRecordValue(record, ['storedFileName', 'StoredFileName', 'originalFileName', 'OriginalFileName', 'fileName', 'FileName'])
    );
  }

  function buildTranscriptStatusOverlay(mediaRecord) {
    if (!mediaRecord) {
      return {};
    }

    const overlay = {
      applicationNumber: getFirstRecordValue(mediaRecord, ['applicationNumber', 'ApplicationNumber']),
      caseId: getFirstRecordValue(mediaRecord, ['caseId', 'CaseId']),
      mediaId: getRecordMediaId(mediaRecord),
      fileType: 'Transcript'
    };

    const transcriptFileId = getFirstRecordValue(mediaRecord, [
      'transcriptFileId',
      'TranscriptFileId',
      'transcriptId',
      'TranscriptId'
    ]);
    if (transcriptFileId) {
      overlay.transcriptFileId = transcriptFileId;
    }

    const storedFileName = getFirstRecordValue(mediaRecord, [
      'transcriptStoredFileName',
      'TranscriptStoredFileName',
      'storedTranscriptFileName',
      'StoredTranscriptFileName'
    ]);
    if (storedFileName) {
      overlay.storedFileName = storedFileName;
    }

    const originalFileName = getFirstRecordValue(mediaRecord, [
      'transcriptOriginalFileName',
      'TranscriptOriginalFileName',
      'originalTranscriptFileName',
      'OriginalTranscriptFileName',
      'transcriptFileName',
      'TranscriptFileName',
      'transcriptName',
      'TranscriptName'
    ]);
    if (originalFileName) {
      overlay.originalFileName = originalFileName;
    }

    const fileSize = getFirstRecordValue(mediaRecord, [
      'transcriptFileSizeInBytes',
      'TranscriptFileSizeInBytes',
      'transcriptFileSizeBytes',
      'TranscriptFileSizeBytes',
      'transcriptSizeInBytes',
      'TranscriptSizeInBytes',
      'transcriptContentLength',
      'TranscriptContentLength',
      'transcriptFileSize',
      'TranscriptFileSize',
      'transcriptSize',
      'TranscriptSize'
    ]);
    if (fileSize) {
      overlay.fileSizeInBytes = fileSize;
    }

    const mimeType = getFirstRecordValue(mediaRecord, [
      'transcriptMimeType',
      'TranscriptMimeType',
      'transcriptContentType',
      'TranscriptContentType'
    ]);
    if (mimeType) {
      overlay.mimeType = mimeType;
    }

    const scanStatus = getFirstRecordValue(mediaRecord, [
      'transcriptScanStatus',
      'TranscriptScanStatus',
      'transcriptScanResult',
      'TranscriptScanResult',
      'transcriptScanResultStatus',
      'TranscriptScanResultStatus',
      'transcriptVirusScanStatus',
      'TranscriptVirusScanStatus',
      'transcriptVirusScanResult',
      'TranscriptVirusScanResult',
      'transcriptAntivirusScanStatus',
      'TranscriptAntivirusScanStatus',
      'transcriptAntiVirusScanStatus',
      'TranscriptAntiVirusScanStatus',
      'transcriptMalwareScanStatus',
      'TranscriptMalwareScanStatus',
      'transcriptMalwareScanResult',
      'TranscriptMalwareScanResult',
      'transcriptThreatScanStatus',
      'TranscriptThreatScanStatus',
      'transcriptThreatScanResult',
      'TranscriptThreatScanResult',
      'transcriptAvScanStatus',
      'TranscriptAvScanStatus',
      'transcriptFileScanStatus',
      'TranscriptFileScanStatus',
      'transcriptScanVerdict',
      'TranscriptScanVerdict',
      'transcriptVerdict',
      'TranscriptVerdict'
    ]);
    if (scanStatus) {
      overlay.scanStatus = scanStatus;
    }

    const lifecycleStatus = getFirstRecordValue(mediaRecord, [
      'transcriptRecordStatus',
      'TranscriptRecordStatus',
      'transcriptStatus',
      'TranscriptStatus',
      'transcriptFileStatus',
      'TranscriptFileStatus',
      'transcriptUploadStatus',
      'TranscriptUploadStatus',
      'transcriptWorkflowStatus',
      'TranscriptWorkflowStatus',
      'transcriptProcessingStatus',
      'TranscriptProcessingStatus'
    ]);
    if (lifecycleStatus) {
      overlay.recordStatus = lifecycleStatus;
    }

    const virusFlag = getFirstRecordValue(mediaRecord, [
      'transcriptHasVirus',
      'TranscriptHasVirus',
      'transcriptVirusDetected',
      'TranscriptVirusDetected',
      'isTranscriptVirusDetected',
      'IsTranscriptVirusDetected',
      'transcriptMalwareDetected',
      'TranscriptMalwareDetected',
      'transcriptThreatDetected',
      'TranscriptThreatDetected',
      'transcriptThreatFound',
      'TranscriptThreatFound',
      'transcriptInfected',
      'TranscriptInfected',
      'transcriptQuarantined',
      'TranscriptQuarantined',
      'isTranscriptQuarantined',
      'IsTranscriptQuarantined'
    ]);
    if (virusFlag !== undefined && virusFlag !== null && String(virusFlag).trim() !== '') {
      overlay.hasVirus = virusFlag;
    }

    return overlay;
  }

  function mergeTranscriptRecordCandidates(...records) {
    const candidates = records
      .filter((record) => isPlainObject(record))
      .filter((record) => hasMeaningfulTranscriptRecord(record));

    if (candidates.length === 0) {
      return null;
    }

    const bestStatusRecord = candidates.reduce((bestRecord, record) => chooseBetterCaseFileRecord(bestRecord, record), null);
    const merged = {};

    // Merge all metadata first so we keep IDs, file names, and size fields from
    // every possible source. Then put the best status candidate last. This avoids
    // a stale parent transcriptScanStatus such as Uploaded/Scanning overwriting a
    // real child transcript scan result such as Clean/Quarantined.
    candidates.forEach((record) => copyNonEmptyRecordFields(merged, record));
    copyNonEmptyRecordFields(merged, bestStatusRecord ?? {});

    return hasMeaningfulTranscriptRecord(merged) ? merged : null;
  }

  function getEmbeddedTranscriptRecord(mediaRecord) {
    if (!mediaRecord) {
      return null;
    }

    const nestedTranscript = getFirstRawRecordValue(mediaRecord, [
      'transcript',
      'Transcript',
      'transcriptFile',
      'TranscriptFile',
      'transcriptRecord',
      'TranscriptRecord',
      'transcriptMetadata',
      'TranscriptMetadata',
      'transcriptAttachment',
      'TranscriptAttachment'
    ]);

    return mergeTranscriptRecordCandidates(
      isPlainObject(nestedTranscript) ? nestedTranscript : null,
      buildTranscriptStatusOverlay(mediaRecord)
    );
  }

  function getEffectiveTranscriptRecord(mediaRecord, transcriptRecord) {
    return mergeTranscriptRecordCandidates(
      transcriptRecord ?? null,
      getEmbeddedTranscriptRecord(mediaRecord),
      buildTranscriptStatusOverlay(mediaRecord)
    );
  }

  function unwrapCaseFileRecord(record) {
    if (!isPlainObject(record)) {
      return record ?? null;
    }

    const mediaRecord = getFirstRawRecordValue(record, ['mediaRecord', 'MediaRecord']);
    if (isPlainObject(mediaRecord)) {
      const normalizedMediaRecord = { ...mediaRecord };
      const playbackWebBaseUrl = getFirstRecordValue(record, ['playbackWebBaseUrl', 'PlaybackWebBaseUrl']);
      if (playbackWebBaseUrl && !getFirstRecordValue(normalizedMediaRecord, ['playbackWebBaseUrl', 'PlaybackWebBaseUrl'])) {
        normalizedMediaRecord.playbackWebBaseUrl = playbackWebBaseUrl;
      }

      const transcriptRecord = getFirstRawRecordValue(record, ['transcriptRecord', 'TranscriptRecord']);
      if (isPlainObject(transcriptRecord) && !getFirstRawRecordValue(normalizedMediaRecord, ['transcriptRecord', 'TranscriptRecord'])) {
        normalizedMediaRecord.transcriptRecord = transcriptRecord;
      }

      return normalizedMediaRecord;
    }

    return record;
  }

  function mapStatusSummaryFilesToCaseFileRecords(summary, config) {
    const summaryFiles = Array.isArray(summary?.files) ? summary.files : [];
    const resolvedApplicationId = String(summary?.applicationId || '').trim();
    const resolvedApplicationNumber = String(config?.applicationNumber || '').trim();
    const resolvedCaseId = String(summary?.caseId || config?.caseId || '').trim();

    return summaryFiles.map((file, index) => {
      const fileName = String(file?.fileName || file?.FileName || '').trim();
      const fileType = String(file?.fileType || file?.FileType || '').trim();
      const status = String(file?.status || file?.Status || '').trim();
      const sizeInBytes = Number(file?.sizeInBytes ?? file?.SizeInBytes ?? 0);
      const normalizedFileType = normalizeValue(fileType);
      const apiMediaFileId = String(file?.mediaFileId || file?.MediaFileId || '').trim();
      const fallbackMediaId = apiMediaFileId || fileName || `${resolvedCaseId}-${normalizedFileType || 'file'}-${index + 1}`;

      return {
        applicationId: resolvedApplicationId,
        applicationNumber: resolvedApplicationNumber,
        caseId: resolvedCaseId,
        mediaId: fallbackMediaId,
        clientApplicationId: String(file?.clientApplicationId || file?.ClientApplicationId || resolvedApplicationId).trim(),
        fileType: fileType || 'Media',
        storedFileName: fileName,
        originalFileName: fileName,
        fileName,
        sizeInBytes: Number.isFinite(sizeInBytes) ? sizeInBytes : 0,
        scanStatus: status,
        status,
        lifecycleStatus: status,
        fileStatus: status,
        updatedAtUtc: summary?.timestamp || summary?.Timestamp || null
      };
    });
  }

  function normalizeCaseFileRecords(records) {
    return (records ?? []).map((record) => unwrapCaseFileRecord(record)).filter(Boolean);
  }

  return {
    buildTranscriptStatusOverlay,
    mergeTranscriptRecordCandidates,
    getEmbeddedTranscriptRecord,
    getEffectiveTranscriptRecord,
    unwrapCaseFileRecord,
    mapStatusSummaryFilesToCaseFileRecords,
    normalizeCaseFileRecords,
    hasMeaningfulTranscriptRecord
  };
}
